import cx from "classnames";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FormType } from "../../../../types/forms";
import AdvancedTaskDto from "../../../../types/dtos/forms/AdvancedTaskDto";
import { ClientTaskType } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { advancedTaskHelper } from "../../../../helpers";
import { calculateTaskWeightingTotal } from "../../../../helpers/taskWeightingHelper";
import Tooltip from "../../../common/Tooltip";

interface TaskWeightingBannerProps {
  tasks: AdvancedTaskDto[];
  taskType: ClientTaskType;
  formType: FormType;
}

function TaskWeightingBanner({
  tasks,
  taskType,
  formType,
}: TaskWeightingBannerProps) {
  const result = calculateTaskWeightingTotal(tasks);
  if (result.includedCount === 0) {
    return null;
  }

  const isCollab = formType === "COLLAB-DOC";
  const taskTypePlural = advancedTaskHelper.ToLowerCase(
    taskType.pluralNameTranslationKeyIdentifier,
  );

  return (
    <div className="mt-1 flex justify-end">
      <div
        className={cx(
          "w-fit max-w-full rounded-md px-3 py-2 text-right",
          isCollab
            ? "bg-gray-50 text-gray-700"
            : "bg-white/10 text-white",
        )}
        role="status"
      >
        <p className="text-base">
          {t("TaskType.Weighting.CurrentTotal", "Current weighting: {{total}}", {
            total: result.formattedTotal,
          })}
        </p>
        {!result.isExactly100 && (
          <p className="mt-1 text-base opacity-90 inline-flex items-center justify-end gap-1.5">
            <span>
              {t(
                "TaskType.Weighting.NormalisedScoreNote",
                "Scores that use {{taskTypePlural}} weightings will adjust them to add up to 100%",
                { taskTypePlural },
              )}
            </span>
            <Tooltip
              appearAt="top"
              align="end"
              className="max-w-[280px]"
              content={t(
                "TaskType.Weighting.NormalisedScoreTooltip",
                "For example, if two {{taskTypePlural}} are both set to 100%, each is treated as 50% so scores stay proportional to how you configured them.",
                { taskTypePlural },
              )}
              triggerElement={
                <span
                  className={cx(
                    "inline-flex shrink-0 cursor-pointer",
                    isCollab
                      ? "text-gray-400 hover:text-gray-600"
                      : "text-white/70 hover:text-white",
                  )}
                  aria-label={t(
                    "TaskType.Weighting.NormalisedScoreTooltip",
                    "For example, if two {{taskTypePlural}} are both set to 100%, each is treated as 50% so scores stay proportional to how you configured them.",
                    { taskTypePlural },
                  )}
                >
                  <FontAwesomeIcon icon={faCircleInfo} />
                </span>
              }
            />
          </p>
        )}
        {result.hasMissingOrInvalid && (
          <p className="mt-1 text-sm opacity-90">
            {t(
              "TaskType.Weighting.MissingValuesNote",
              "Some tasks are missing a weighting value and count as 0%.",
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export default TaskWeightingBanner;
